import { Alert, Drawer, Empty, Result, Skeleton, Tag } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { getCreator } from '../api';
import type { CompareMetric, CreatorDetailResponse, CreatorFacts } from '../contract';
import {
  KIND_LABEL,
  PACE_META,
  creatorName,
  fmtDay,
  fmtHours,
  fmtKc,
  fmtMonth,
  fmtNum,
  errorText,
  goalGapText,
  isMonthEnded,
  secondsToHours,
} from '../format';
import CompareBlock from './compare-block';
import { DailyTable, HistoryBars } from './creator-detail-parts';
import CreatorMoreCards from './creator-more-cards';
import { DualAxisLine } from './daily-line';
import InsightsCard from './insights-card';
import { CompareLine } from './kpi-card';
import MixDonut from './mix-donut';
import MonthHeatmap, { cellsFromSeries } from './month-heatmap';
import { TiktokMonthTable } from './tiktok-month-panel';
import styles from '../style.module.scss';

interface Props {
  uid: string | null;
  month: string;
  onClose: () => void;
}

/** Khối "Để đạt Bậc X còn thiếu …" — ẩn khi không có mốc (Studio / NPC / Nhóm nhảy). */
const GoalBlock = ({ facts }: { facts: CreatorFacts }) => {
  const { goal, pace } = facts;
  // Tháng đã kết thúc (06 E6a): không còn "tiến độ", chỉ còn kết quả tháng.
  if (isMonthEnded(facts.monthEnded, facts.daysRemaining)) {
    const lv = facts.levelReached;
    if (lv === null || lv === undefined) return null;
    return lv > 0 ? (
      <Alert type="success" showIcon message={<strong>Kết thúc tháng ở Bậc {lv}</strong>} />
    ) : (
      <Alert type="info" showIcon message={<strong>Kết thúc tháng chưa đạt Bậc 1</strong>} />
    );
  }
  if (!goal || !pace) return null;
  const gap = goalGapText(goal.level, pace);
  if (pace.status === 'DONE' || !gap) {
    return (
      <Alert type="success" showIcon message={<strong>Đã đủ điều kiện Bậc {goal.level}</strong>} />
    );
  }
  const meta = PACE_META[pace.status];
  return (
    <div className={styles.goal}>
      <div className={styles.goalHead}>
        <strong>Mục tiêu Bậc {goal.level}</strong>
        {meta && <Tag color={meta.color}>{meta.label}</Tag>}
      </div>
      <p className={styles.goalText}>{gap}</p>
      <p className={styles.muted}>
        Điều kiện Bậc {goal.level}: {fmtKc(goal.kc)} KC · {fmtNum(goal.days)} ngày hợp lệ ·{' '}
        {fmtHours(goal.hours)}. Còn {fmtNum(facts.daysRemaining)} ngày trong tháng
        {pace.daysFeasible ? '.' : ' — không đủ ngày để đạt.'}
      </p>
    </div>
  );
};

const Stat = ({ label, value, sub }: { label: string; value: string; sub: ReactNode }) => (
  <div className={styles.kpi}>
    <span className={styles.kpiLabel}>{label}</span>
    <span className={styles.kpiValue}>{value}</span>
    <span className={styles.kpiSub}>{sub}</span>
  </div>
);

const Body = ({ data, month }: { data: CreatorDetailResponse; month: string }) => {
  const f = data.facts;
  if (!f) {
    return (
      <Empty description={`Creator này chưa có số liệu trong ${fmtMonth(month).toLowerCase()}.`} />
    );
  }
  const prev = f.prevMonth;
  const prevLabel = prev ? fmtMonth(prev.month).toLowerCase() : 'tháng trước';
  const hasSeries = f.series.days.length > 0;
  const byMonthFile = f.basis === 'MONTH_FILE';
  const cmp = f.compare;
  /** Dòng so sánh của một chỉ số: theo `compare` (đợt 1b); ảnh chụp cũ không có → `prevMonth` (đợt 1). */
  const line = (key: CompareMetric) => {
    if (cmp) {
      return (
        <CompareLine
          pct={cmp.metrics[key]?.pct}
          prevN={cmp.n}
          mode={cmp.mode}
          source={cmp.label}
          label={prevLabel}
        />
      );
    }
    const old =
      key === 'diamonds' || key === 'liveSeconds' || key === 'liveDays' ? prev?.pct[key] : null;
    return (
      <CompareLine pct={old} prevN={prev?.n} label={prevLabel} empty={prev ? null : f.compareNote} />
    );
  };
  return (
    <div className={styles.drawerBody}>
      <div className={styles.drawerMeta}>
        <Tag color="magenta">{KIND_LABEL[f.kind] || f.kind}</Tag>
        {f.groupName && <span>{f.groupName}</span>}
        {f.rank.diamonds !== null && (
          <span>
            Hạng KC <strong>#{fmtNum(f.rank.diamonds)}</strong> / {fmtNum(f.rank.total)}
          </span>
        )}
        <span className={styles.muted}>
          {byMonthFile
            ? `Theo file tháng TikTok${f.tiktokMonth ? ` (${f.tiktokMonth.period})` : ''}`
            : `Số liệu đến ${fmtDay(f.asOfDay)}`}
        </span>
      </div>

      <div className={styles.statGrid}>
        <Stat label="Kim cương" value={fmtKc(f.totals.diamonds)} sub={line('diamonds')} />
        <Stat
          label="Giờ LIVE"
          value={fmtHours(secondsToHours(f.totals.liveSeconds))}
          sub={line('liveSeconds')}
        />
        <Stat label="Ngày hợp lệ" value={fmtNum(f.totals.liveDays)} sub={line('liveDays')} />
        <Stat
          label="Follower mới"
          value={fmtNum(f.totals.newFollowers)}
          sub={
            cmp
              ? line('newFollowers')
              : `${fmtNum(f.totals.pkSessions)} trận PK · ${fmtNum(
                  f.totals.liveSessions
                )} phiên LIVE`
          }
        />
      </div>

      <GoalBlock facts={f} />

      <InsightsCard insights={data.insights} max={6} />

      <CompareBlock compare={cmp} note={f.compareNote} />

      {f.tiktokMonth && <TiktokMonthTable t={f.tiktokMonth} />}

      <CreatorMoreCards f={f} />

      {hasSeries ? (
        <>
          <section className={styles.card} aria-label="Theo ngày">
            <h2 className={styles.cardTitle}>Kim cương & giờ LIVE theo ngày</h2>
            <DualAxisLine
              days={f.series.days}
              diamonds={f.series.diamonds}
              liveSeconds={f.series.liveSeconds}
            />
          </section>
          <MonthHeatmap
            cells={cellsFromSeries(f.month, f.daysInMonth, f.series)}
            asOfDay={f.asOfDay}
          />
        </>
      ) : (
        <Empty
          description={
            byMonthFile
              ? 'Tháng này tính theo file tháng TikTok — không có số theo ngày.'
              : 'Tháng này chỉ còn số tổng (chuỗi theo ngày chỉ giữ cho tháng hiện tại và tháng trước).'
          }
        />
      )}

      <MixDonut
        mix={{
          pk: f.mix.pkDiamonds,
          other: f.mix.otherDiamonds,
          overseas: f.mix.overseasDiamonds,
          multiGuest: f.mix.multiGuestDiamonds,
          fanClub: f.mix.fanClubDiamonds,
        }}
      />

      {hasSeries && <DailyTable series={f.series} more={f.more?.series} />}

      <HistoryBars history={data.history} current={f} />
    </div>
  );
};

/** Drawer chi tiết một Creator (đặc tả §8): rộng min(720px, 100vw). */
const CreatorDrawer = ({ uid, month, onClose }: Props) => {
  const [data, setData] = useState<CreatorDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    getCreator(uid, month)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(errorText(err, 'Không tải được chi tiết Creator.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, month]);

  const facts = data?.facts;
  const title = facts ? creatorName(facts) : uid ? `UID ${uid}` : '';

  return (
    <Drawer
      open={!!uid}
      onClose={onClose}
      width="min(720px, 100vw)"
      title={<span className={styles.drawerTitle}>{title}</span>}
      destroyOnClose
    >
      {loading && <Skeleton active paragraph={{ rows: 8 }} />}
      {!loading && error && (
        <Result status="info" title="Chưa xem được Creator này" subTitle={error} />
      )}
      {!loading && !error && data && <Body data={data} month={month} />}
    </Drawer>
  );
};

export default CreatorDrawer;
